import { useState } from "react";
import { CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useSeo } from "../lib/seo";
import { CITIES, LISTING_TYPES, TIMELINES, IMAGES } from "../lib/constants";
import { PageHero, Field } from "../components/Common";
import { PageFade } from "../components/Motion";

const EMPTY = { owner_name: "", company: "", phone: "", email: "", address: "", city: "", county: "", property_type: "", asking_price: "", estimated_value: "", sqft: "", lot_size: "", units: "", occupancy: "", current_income: "", current_expenses: "", condition: "", mortgage_info: "", reason: "", timeline: "", comments: "" };

const Group = ({ title, children }) => (
  <fieldset className="border-t border-navy/10 pt-8">
    <legend className="eyebrow pr-4">{title}</legend>
    <div className="mt-6 grid gap-5 sm:grid-cols-2">{children}</div>
  </fieldset>
);

export default function Sell() {
  useSeo("Sell Your Oregon Commercial Property", "Considering selling a commercial property in Oregon? Tell Sido Holdings about it — we may be able to connect it with an investor looking for an opportunity like yours.");
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const I = (k, props = {}) => <input className="field" value={f[k]} onChange={set(k)} data-testid={`seller-${k.replace(/_/g, "-")}`} {...props} />;

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    setUploading(true);
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads/seller", fd);
        setFiles((s) => [...s, data]);
      } catch (err) {
        toast.error(`${file.name}: ${errMsg(err)}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/seller-submissions", { ...f, file_ids: files.map((x) => x.id) });
      setDone(true);
      window.scrollTo({ top: 300, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageFade>
      <PageHero eyebrow="For Owners & Sellers" title="Have a Commercial Property to Sell?" image={IMAGES.concrete}
        sub="Tell us about the property. We may be able to connect it with an investor looking for an opportunity like yours." />
      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x max-w-4xl">
          {done ? (
            <div className="bg-navy text-paper p-8 sm:p-14" data-testid="seller-success">
              <CheckCircle2 className="h-10 w-10 text-gold" strokeWidth={1.5} />
              <p className="mt-8 font-serif text-2xl sm:text-3xl leading-snug">Thank you. We've received your property information. Sido Holdings will review the opportunity and contact you if it appears to fit our current investor requirements.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-navy/10 p-5 sm:p-10 space-y-10" data-testid="seller-form">
              <Group title="Owner">
                <Field label="Owner name *">{I("owner_name", { required: true, autoComplete: "name" })}</Field>
                <Field label="Company">{I("company")}</Field>
                <Field label="Phone *">{I("phone", { required: true, type: "tel", inputMode: "tel" })}</Field>
                <Field label="Email *">{I("email", { required: true, type: "email" })}</Field>
              </Group>
              <Group title="Property">
                <Field label="Property address *" className="sm:col-span-2">{I("address", { required: true })}</Field>
                <Field label="City *">
                  <select required className="field" value={f.city} onChange={set("city")} data-testid="seller-city"><option value="">Select…</option>{CITIES.map((c) => <option key={c}>{c}</option>)}</select>
                </Field>
                <Field label="County">{I("county")}</Field>
                <Field label="Property type *">
                  <select required className="field" value={f.property_type} onChange={set("property_type")} data-testid="seller-property-type"><option value="">Select…</option>{LISTING_TYPES.map((c) => <option key={c}>{c}</option>)}</select>
                </Field>
                <Field label="Property condition">
                  <select className="field" value={f.condition} onChange={set("condition")} data-testid="seller-condition"><option value="">Select…</option>{["Excellent", "Good", "Fair", "Needs work", "Major repairs needed"].map((c) => <option key={c}>{c}</option>)}</select>
                </Field>
                <Field label="Square footage">{I("sqft", { inputMode: "numeric" })}</Field>
                <Field label="Lot size">{I("lot_size")}</Field>
                <Field label="Units">{I("units", { inputMode: "numeric" })}</Field>
                <Field label="Occupancy (%)">{I("occupancy", { inputMode: "decimal" })}</Field>
              </Group>
              <Group title="Financials">
                <Field label="Asking price ($)">{I("asking_price", { inputMode: "numeric" })}</Field>
                <Field label="Estimated value ($)">{I("estimated_value", { inputMode: "numeric" })}</Field>
                <Field label="Current annual income ($)">{I("current_income", { inputMode: "numeric" })}</Field>
                <Field label="Current annual expenses ($)">{I("current_expenses", { inputMode: "numeric" })}</Field>
                <Field label="Mortgage information (optional)" hint="Only if you choose to share." className="sm:col-span-2">{I("mortgage_info")}</Field>
              </Group>
              <Group title="Sale">
                <Field label="Reason for selling">{I("reason")}</Field>
                <Field label="Desired closing timeline">
                  <select className="field" value={f.timeline} onChange={set("timeline")} data-testid="seller-timeline"><option value="">Select…</option>{TIMELINES.map((c) => <option key={c}>{c}</option>)}<option>Flexible</option></select>
                </Field>
                <Field label="Additional comments" className="sm:col-span-2"><textarea className="field min-h-[120px]" value={f.comments} onChange={set("comments")} data-testid="seller-comments" /></Field>
              </Group>
              <fieldset className="border-t border-navy/10 pt-8">
                <legend className="eyebrow pr-4">Photos & Documents</legend>
                <label className="mt-6 flex flex-col items-center justify-center gap-2 border border-dashed border-navy/25 p-8 text-center cursor-pointer hover:border-gold transition-colors" data-testid="seller-upload-dropzone">
                  {uploading ? <Loader2 className="h-6 w-6 animate-spin text-gold" /> : <Paperclip className="h-6 w-6 text-gold" />}
                  <span className="text-sm text-navy font-semibold">Add photos or documents</span>
                  <span className="text-xs text-mist">JPG, PNG, WEBP, PDF, DOCX, XLSX, CSV · up to 15MB each · stored securely</span>
                  <input type="file" multiple className="hidden" onChange={upload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" data-testid="seller-file-input" />
                </label>
                {files.length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="seller-file-list">
                    {files.map((x) => (
                      <li key={x.id} className="flex items-center justify-between bg-paper px-4 py-2.5 text-sm">
                        <span className="truncate">{x.name}</span>
                        <button type="button" onClick={() => setFiles(files.filter((y) => y.id !== x.id))} aria-label="Remove"><X className="h-4 w-4 text-mist" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </fieldset>
              <button disabled={busy || uploading} className="btn-primary w-full sm:w-auto !px-10" data-testid="seller-submit">{busy ? "Submitting…" : "Submit Property"}</button>
              <p className="text-xs text-mist">Submitting a property does not create a listing agreement or obligation. Information you share is kept confidential and used to evaluate fit with current investor requirements.</p>
            </form>
          )}
        </div>
      </section>
    </PageFade>
  );
}
